import { type HansardSection, type ParliamentaryDay } from '@prisma/client'
import { Link, useRouteLoaderData } from '@remix-run/react'
import { ChevronDown, Slash } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '#app/components/ui/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import {
  type NestedSection,
  nestSections,
} from '#app/models.client/sections/index.js'
import Calendar from './Calendar'
import { type loader as sectionLoader } from './section.$sectionId/index.js'

export function SectionItem({
  day,
  section,
}: {
  day: string
  section: NestedSection
}) {
  return section.children.length > 0 ? (
    <DropdownMenuSub key={section.id}>
      <DropdownMenuSubTrigger>
        <Link to={`/hansard/${day}/section/${section.id}`}>
          {section.title}
        </Link>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          {section.children.map(s => (
            <SectionItem key={s.id} day={day} section={s} />
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  ) : (
    <DropdownMenuItem asChild>
      <Link to={`/hansard/${day}/section/${section.id}`}>{section.title}</Link>
    </DropdownMenuItem>
  )
}

export default function Breadcrumbs({
  day,
  allDays,
  allSections,
}: {
  day: string
  allDays: ParliamentaryDay[]
  section: HansardSection | null
  allSections: HansardSection[]
}) {
  const routeData = useRouteLoaderData<typeof sectionLoader>(
    'routes/hansard+/$day+/section.$sectionId/index',
  )
  const currentSection = routeData?.section
  const sections = nestSections(allSections)

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/hansard">Home</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator>
          <Slash />
        </BreadcrumbSeparator>
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1">
              {day}
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <Calendar allDays={allDays} day={day} />
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
        <BreadcrumbSeparator>
          <Slash />
        </BreadcrumbSeparator>
        <BreadcrumbItem>
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1">
              {currentSection ? currentSection.title : 'Sections'}
              <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {sections.map(s => (
                <SectionItem key={s.id} day={day} section={s} />
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  )
}
